
import { useState } from "react";
import { Image as ImageIcon, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnalysisUploader } from "@/components/analysis";
import { ImageManipulationViewer } from "@/components/analysis/ImageManipulationViewer";
import AnalysisResults, { ResultLevel } from "@/components/AnalysisResults";
import { analyzeImage } from "@/services/fraudDetection/imageAnalysis/core";
import { useToast } from "@/hooks/use-toast";
import { fileToBase64 } from "@/services/fraudDetection/imageAnalysis/utils";

const ImageAnalysis = () => {
  const [results, setResults] = useState<{
    results: {
      type: string;
      level: ResultLevel;
      score: number;
      details: string;
    }[];
    overallStatus: ResultLevel;
    flags?: string[];
    modelName?: string;
    explanation?: string;
    imageMetrics?: any;
    manipulations?: Array<{
      manipulation_type: string;
      region_x: number | null;
      region_y: number | null;
      region_width: number | null;
      region_height: number | null;
      confidence: number;
      description: string;
      detection_method: string;
    }>;
  } | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleAnalyze = async (file: File | string) => {
    setIsLoading(true);
    try {
      const base64Data = typeof file === 'string' ? file : await fileToBase64(file);
      setCurrentImage(base64Data);

      const analysisResult = await analyzeImage(file);

      const manipulations = [
        ...(analysisResult.detectedManipulations || []).map(m => ({
          manipulation_type: m.type,
          region_x: m.region?.x || null,
          region_y: m.region?.y || null,
          region_width: m.region?.width || null,
          region_height: m.region?.height || null,
          confidence: m.confidence,
          description: m.description,
          detection_method: 'manipulation_detection'
        })),
        ...(analysisResult.visualAnomalies || []).map(a => ({
          manipulation_type: a.type,
          region_x: a.region?.x || null,
          region_y: a.region?.y || null,
          region_width: a.region?.width || null,
          region_height: a.region?.height || null,
          confidence: a.severity,
          description: a.description,
          detection_method: a.detectionMethod
        }))
      ];

      const detectionResults = [
        {
          type: "AI Generation",
          level: analysisResult.aiGenerationScore > 0.7 ? "fraudulent" as ResultLevel :
                 analysisResult.aiGenerationScore > 0.4 ? "suspicious" as ResultLevel :
                 "safe" as ResultLevel,
          score: Math.round(analysisResult.aiGenerationScore * 100),
          details: "Analysis of potential AI-generated content.",
        },
        {
          type: "Manipulation",
          level: analysisResult.manipulationScore > 0.7 ? "fraudulent" as ResultLevel :
                 analysisResult.manipulationScore > 0.4 ? "suspicious" as ResultLevel :
                 "safe" as ResultLevel,
          score: Math.round(analysisResult.manipulationScore * 100),
          details: "Detection of image editing and manipulation.",
        },
        {
          type: "Visual Consistency",
          level: analysisResult.visualConsistencyScore > 0.7 ? "fraudulent" as ResultLevel :
                 analysisResult.visualConsistencyScore > 0.4 ? "suspicious" as ResultLevel :
                 "safe" as ResultLevel,
          score: Math.round(analysisResult.visualConsistencyScore * 100),
          details: "Analysis of lighting, shadows, and perspective consistency.",
        },
      ];
      
      const riskLevels = {
        "fraudulent": 2,
        "suspicious": 1,
        "safe": 0
      };
      
      const maxRiskLevel = detectionResults.reduce((max, result) => {
        return Math.max(max, riskLevels[result.level]);
      }, 0);
      
      const overallStatus = Object.keys(riskLevels).find(
        key => riskLevels[key as keyof typeof riskLevels] === maxRiskLevel
      ) as ResultLevel;
      
      setResults({
        results: detectionResults,
        overallStatus,
        flags: analysisResult.flags,
        modelName: analysisResult.modelName,
        explanation: analysisResult.explanation,
        imageMetrics: analysisResult.imageMetrics,
        manipulations
      });
      
      toast({
        title: "Analysis Complete",
        description: `Analysis completed with ${overallStatus} result`,
        variant: overallStatus === "fraudulent" ? "destructive" : overallStatus === "suspicious" ? "default" : "default"
      });
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast({
        title: "Analysis Failed",
        description: "There was an error analyzing the image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (!results) return;
    
    const exportData = {
      analysisType: "Image Analysis",
      timestamp: new Date().toISOString(),
      results: results.results,
      overallStatus: results.overallStatus,
      explanation: results.explanation,
      modelName: results.modelName,
      flags: results.flags,
      imageMetrics: results.imageMetrics,
      manipulations: results.manipulations
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `image-analysis-${new Date().toISOString()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    toast({
      title: "Export Complete",
      description: "Analysis results have been exported",
      variant: "default"
    });
  };

  const handleNewAnalysis = () => {
    setResults(null);
    setCurrentImage(null);
  };

  const ResultsContent = () => (
    <>
      <AnalysisResults
        results={results?.results || []}
        overallStatus={results?.overallStatus || "safe"}
      />
      
      {currentImage && results?.manipulations && results.manipulations.length > 0 && (
        <ImageManipulationViewer
          imageUrl={currentImage}
          manipulations={results.manipulations}
        />
      )}

      <div className="flex gap-2 mt-4">
        <Button onClick={handleNewAnalysis}>New Analysis</Button>
        <Button variant="outline" onClick={handleExport}>
          Export Results
        </Button>
      </div>
    </>
  );

  return (
    <div className="container mx-auto p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-6 w-6" />
            Image Analysis
          </CardTitle>
          <CardDescription>
            Upload an image to analyze it for potential manipulation or AI generation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!results ? (
            <AnalysisUploader
              onAnalyze={handleAnalyze}
              isLoading={isLoading}
              acceptedFileTypes="image/*"
              maxSize={15 * 1024 * 1024} // 15MB
              title="Image Analysis"
              description="Upload an image to analyze"
              icon={<ImageIcon className="h-5 w-5" />}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Analysis Results
                </h3>
                <Button variant="outline" size="sm" onClick={handleNewAnalysis}>
                  New Analysis
                </Button>
              </div>
              
              <div className="p-4 border rounded-lg bg-card">
                <ResultsContent />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ImageAnalysis;
